import { AppId } from './app';

/**
 * Login provider type, rendered by genesis
 * and retrieved from SYN
 */
export interface LoginProviderSyn {
    type: string;
    state: string;
    url: string;
    name: string;
    id: string;
    buttonColor: string | null;
    logoUrl: string | null;
}

export interface LoginGuidelines {
    text: any;
    url: string;
}
export type PendoExtraProperties = {
    account?: { [key: string]: any };
    visitor?: { [key: string]: any };
    [key: string]: any | undefined;
};

export enum ToastType {
    SUCCESS = 'success',
    WARNING = 'warning',
    INFORMATION = 'information',
    ERROR = 'error',
}

export interface CommonContext {
    environment: 'local' | 'develop' | 'staging' | 'production';
    token: string;
    expiresAt?: string;
    refreshToken(): Promise<string>;
    tenantUrl: string;
    locale: string;
    primaryColor: string;
    fontFamily: string;
    showToast(text: string, type: ToastType): void;
}

export interface BeekeeperContext extends CommonContext {
    vanityUrl: string;
    site: string;
    lumappsJwt: string;
    pageClose(): void;
}

export type RouterBridgeVersion = '1.0.0';

export interface NavigationOptions {
    /**
     * If set, external URLs will be opened in a new tab.
     *
     * @see RouterBridge.isInternalUrl
     */
    openExternalUrlInNewTab?: boolean;
}

export interface GetURLOptions {
    type?: 'relative' | 'absolute';
}

/**
 * Function that determines how to resolve a routing change.
 * It can be called with either:
 * - next()       : The navigation is validated and either the next hook is called or the navigation is allowed to proceed.
 * - next(false)  : Prevent the routing. The URL won't change and no re-rending will be executed, no additional hooks will be called.
 * - next('/path'): Prevent the routing and instead redirect to the given path. No additional hooks will be called.
 */
export type NextFunction = (res?: false | string) => void;

export interface ListenerReference {
    /**
     * Unregister the router hook mapped to this reference.
     */
    stop(): void;
}

/**
 * Instance of a URL route
 */
export interface BridgeRoute {
    path: string;
    params: Record<string, string>;
    query: URLSearchParams;
}

export interface RouterBridge {
    /**
     * Semantical version of the specification supported by this implementation.
     */
    version: RouterBridgeVersion;
    /**
     * Base path for the current app.
     */
    basePath: string;
    /**
     * Instructs the router to navigate to the given path.
     *
     * @param path Path (absolute or relative) to navigate to.
     * @param options.openExternalUrlInNewTab If set, external URLs will be opened in a new tab
     */
    push(path: string, options?: NavigationOptions): void;
    /**
     * Creates a URL for the given app and url.
     * @param appId - target app
     * @param url - url to append
     * @returns string
     */
    getUrl?: (appId: AppId, url: string, options?: GetURLOptions) => string;
    /**
     * Similar as push, but this doesn't add an entry to the History.
     *
     * @param path Path to navigate to.
     */
    replace(path: string): void;

    /**
     * Navigate through the browser History.
     *
     * @param n History steps to navigate. Can be negative (e.g. -1).
     */
    go(n: number): void;
    /**
     * Check if a given path is internal or not.
     * A path is considered internal if this router implementation can navigate to it.
     *
     * This is only true if the path has the correct domain (in case of absolute URLs) and the
     * path points to a valid, routable view.
     *
     * @param path Path (absolute or relative) to check
     */
    isInternalUrl(path: string): boolean;
    /**
     * Get the current route.
     */
    currentRoute(): BridgeRoute;
    /**
     * Register a hook that allows to listen for route changes.
     *
     * This hook will be scoped to the MicroFrontend application, meaning it will only
     * be called for route changes where either (or both) `from` and `to` map to a route
     * declared by this MicroFrontend.
     *
     * If multiple hooks are registered, they will be called in the order they were registered.
     *
     * Important: The next function has to be called exactly once no matter the circumstances.
     *
     * @param hook Hook called whenever the route changes.
     *
     * @returns A reference to the hook that can be used to unregister it.
     *
     * @see NextFunction
     */
    onBeforeRouteUpdate(hook: (to: BridgeRoute, from: BridgeRoute, next: NextFunction) => void): ListenerReference;

    /**
     * Register a hook that is triggered after a successful navigation change.
     *
     * This hook will be scoped to the MicroFrontend application, meaning it will only
     * be called for route changes where either (or both) `from` and `to` map to a route
     * declared by this MicroFrontend.
     *
     * @param hook Hook called after the route changes.
     *
     * @returns A reference to the hook that can be used to unregister it.
     */
    onAfterRouterUpdate(hook: (to: BridgeRoute, from: BridgeRoute) => void): ListenerReference;
}
